import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Card, CircularProgress, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DataGrid, enUS, frFR, GridCellParams, GridColDef, GridToolbar } from '@mui/x-data-grid';
import moment from 'moment';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AuthHeader } from '../../../../utils/helpers/auth-headers';
import './RealTimeList.scss';
import { AlertSeverity } from '../../../../utils/enum/Alerts';
import { setAlertData } from '../../../../stores/alertStore';
import { RootState } from '../../../../stores/globalStore';
import { PageRowsOptions } from '../../../../models/DataGridConf';
import { setConnectedUser } from '../../../../stores/authenticationStore';

interface HistoryMeeting {
  meetingName: string;
  participantCount: number;
  start: string;
  uid: string;
  worstQuality: string;
}

export default function RealTimeList() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const i18nLang = i18next.language;
  const [gridLang, setGridLang] = useState(frFR);

  const connectedUser = useSelector((state: RootState) => state.authentication.connectedUser);

  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(true);

  const path = useLocation().pathname;
  const [pathName, setPathName] = useState(path);

  const paramsRoute = useParams();

  const [pageSize, setPageSize] = useState<number>(connectedUser.prefs?.nbRow ? connectedUser.prefs.nbRow : 25);
  const pageRowsOptions = PageRowsOptions;

  const [liveMeetings, setLiveMeetings] = useState<HistoryMeeting[]>([]);

  const [selectionModel, setSelectionModel] = useState<any[]>([]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      hide: true
    },
    {
      field: 'name',
      renderHeader: () => (
        <Tooltip title={t('RealTimeList.table.name') as string} arrow>
          <span className="strong-header">{t('RealTimeList.table.name')}</span>
        </Tooltip>
      ),
      minWidth: 280,
      type: 'string',
      flex: 1
    },
    {
      field: 'duration',
      renderHeader: () => (
        <Tooltip title={t('RealTimeList.table.duration') as string} arrow>
          <span className="strong-header">{t('RealTimeList.table.duration')}</span>
        </Tooltip>
      ),
      minWidth: 60,
      maxWidth: 120,
      type: 'dateTime',
      flex: 1,
      renderCell: (params) => {
        const now = moment();
        const time = moment(params.row.duration);
        const duration = moment.duration(now.diff(time));
        return (
          <p>
            {duration.hours()}
            h
            {duration.minutes()}
            m
          </p>
        );
      }
    },
    {
      field: 'date',
      renderHeader: () => (
        <Tooltip title={t('RealTimeList.table.startTime') as string} arrow>
          <span className="strong-header">{t('RealTimeList.table.startTime')}</span>
        </Tooltip>
      ),
      minWidth: 170,
      maxWidth: 170,
      type: 'dateTime',
      flex: 1,
      renderCell: ((params) => {
        const date = new Date(params.row.date).toLocaleString(i18nLang);
        return (<p>{date}</p>);
      })
    },
    {
      field: 'worstQuality',
      renderHeader: () => (
        <Tooltip title={t('RealTimeList.table.quality') as string} arrow>
          <span className="strong-header">{t('RealTimeList.table.quality')}</span>
        </Tooltip>
      ),
      minWidth: 100,
      maxWidth: 110,
      type: 'string',
      flex: 1,
      renderCell: ((params) => {
        let formattedValue;
        switch (params.row.worstQuality) {
        case '0_unknown':
          formattedValue = <p style={{ color: '#94A2B0' }}>{ t('RealTimeList.table.qualityList.unknown') }</p>;
          break;
        case '1_good':
          formattedValue = <p style={{ color: '#93B37A' }}>{ t('RealTimeList.table.qualityList.good') }</p>;
          break;
        case '2_ok':
          formattedValue = <p style={{ color: '#E1BC66' }}>{ t('RealTimeList.table.qualityList.ok') }</p>;
          break;
        case '3_bad':
          formattedValue = <p style={{ color: '#d77947' }}>{ t('RealTimeList.table.qualityList.bad') }</p>;
          break;
        case '4_terrible':
          formattedValue = <p style={{ color: '#E05D5F' }}>{ t('RealTimeList.table.qualityList.terrible') }</p>;
          break;
        default:
          break;
        }
        return formattedValue;
      })

    },
    {
      field: 'nbParticipants',
      renderHeader: () => (
        <Tooltip title={t('RealTimeList.table.participants') as string} arrow>
          <span className="strong-header">{t('RealTimeList.table.participants')}</span>
        </Tooltip>
      ),
      minWidth: 60,
      maxWidth: 100,
      flex: 1
    }
  ];

  interface GridRowDefPexip {
    id: string,
    name: string,
    duration: string,
    date: string,
    worstQuality: string
    nbParticipants: number
  }

  function buildRows(data: any) {
    const rows: GridRowDefPexip[] = [];
    for (let i = 0; i < data.length; i += 1) {
      const row = {
        id: data[i]._id,
        name: data[i].meetingName,
        duration: data[i].start,
        date: data[i].start,
        worstQuality: data[i].worstQuality,
        nbParticipants: data[i].participantCount
      };
      rows.push(row);
    }
    return rows;
  }

  useEffect(() => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/liveMeeting`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => setLiveMeetings(response.data.data))
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      })
      .then(() => setLoading(false));
  }, [paramsRoute]);

  useEffect(() => {
    if (!path.includes('/real-time/')) {
      setSelectionModel([]);
    } else {
      setSelectionModel([paramsRoute.meetingId]);
    }
  }, [navigate]);

  useEffect(() => {
    setPathName(path);
  }, [path]);

  useEffect(() => {
    if (i18nLang === 'en') {
      setGridLang(enUS);
    } else {
      setGridLang(frFR);
    }
  }, [i18nLang]);

  const updateNbRowUser = (updateNbRow: number) => {  
    const updatePrefs = { ...connectedUser.prefs };
    updatePrefs.nbRow = updateNbRow;
    const modifyPrefs = {
      prefs: updatePrefs
    };
    axios.post(`${window._env_.REACT_APP_API_URI}/user/prefs`, modifyPrefs, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        dispatch(setConnectedUser({ connectedUser: response.data.data }));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const changePageSize = (newPage: number) => {
    setPageSize(newPage);
    updateNbRowUser(newPage);
  };

  return (
    <div className={pathName.includes('/real-time/') ? 'real-time-bloc outlet-open' : 'real-time-bloc'}>
      <Card className="category-container" sx={{ height: 651, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <div style={{ height: 651 }}>
            <DataGrid
              rows={buildRows(liveMeetings)}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={pageRowsOptions}
              onPageSizeChange={(newPage) => changePageSize(newPage)}
              components={{ Toolbar: GridToolbar }}
              pagination
              onCellClick={(params: GridCellParams) => {
                navigate(`./${params.row.id}`);
              }}
              getRowClassName={() => 'pointer'}
              hideFooterSelectedRowCount
              onSelectionModelChange={setSelectionModel}
              selectionModel={selectionModel}
              localeText={gridLang.components.MuiDataGrid.defaultProps.localeText}
              density="compact"
            />
          </div>
        )}
      </Card>
      <div className="category-container">
        <Outlet />
      </div>
    </div>
  );
}
